import React, { MutableRefObject, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { useModal } from 'hooks/useModal';
import { ModalsKeys, Nullable } from 'global';
import { FormProvider } from 'react-hook-form';
import { Equipment } from 'modules/desking/types';
import { ChargeAttachType } from 'modules/equipment/types';
import { ListingForm } from 'components/shared/TableListing/types';
import { Listing, useListing } from 'components/shared/TableListing';
import {
  newChargeRow,
  useEquipmentActions,
  chargeTypesByLabels,
  generateEquipmentName,
  getCalculatedTotalsByLevel,
} from 'modules/equipment';
import { invoiceDetailsListingColumns } from './invoiceDetailsListingConfig';
import {
  buildBadgeLabel,
  mapQuoteEquipmentToSync,
  mapEquipmentToInvoiceTable,
  checkingEquipmentUpgradeNeeds,
  invoiceListingDefaultActionPanel,
  mapEquipmentAndQuoteListToInvoiceTable,
} from '../../../lib';
import { useEquipmentAssets } from 'modules/equipment/api';
import {
  Quote,
  QuoteChargeType,
  CalculatedTotalsByLevel,
  InvoiceListingActions,
  QuoteChargeListingEntity,
  QuoteEquipmentWithCharge,
  QuoteChargeListingContext,
} from '../../../types';
import { QuoteFeeType } from '@trnsact/trnsact-shared-types';
import { quoteChargeCalculator } from '../../../lib/quoteChargeCalculator';

interface Props {
  VOId: string;
  dynamicsVOId: string;
  quote: Nullable<Quote>;
  equipmentList: Equipment[];
  onSubscribeTotals: (calc: CalculatedTotalsByLevel, fields?: QuoteChargeListingEntity[]) => void;
  chargesSubmitRef: MutableRefObject<{ submit: () => ListingForm<QuoteChargeListingEntity> } | null>;
  actionPanelBottom?: (actions: InvoiceListingActions) => React.ReactNode;
}

export const InvoiceDetailsListing = ({
  VOId,
  quote,
  dynamicsVOId,
  chargesSubmitRef,
  onSubscribeTotals,
  equipmentList = [],
  actionPanelBottom = invoiceListingDefaultActionPanel,
}: Props) => {
  const [selectedImages, setSelectedImages] = useState<File[]>([]);

  const { onDeleteEquipment, onUpdateEquipment, onCreateEquipment, isAnyActionInProgress } = useEquipmentActions();
  const { handleUploadImages } = useEquipmentAssets();

  const [priceCalc, setPriceCalc] = useState<QuoteChargeListingEntity[]>([]);

  const { handleOpen: handleOpenEquipmentDialog, handleClose: handleCloseEquipmentDialog } = useModal(
    ModalsKeys.CreateUpdateEquipmentDialog
  );
  const { handleOpen: onOpenCreateChargeDialog } = useModal(ModalsKeys.CreateChargesEquipmentDialog);
  const { handleOpen: handleOpenChargesDialog, handleClose } = useModal(ModalsKeys.ChargesEquipmentDialog);
  const { handleOpen: handleOpenConfirmationDialog } = useModal(ModalsKeys.Confirmation);

  const initialFields = useMemo(() => {
    if (!equipmentList.length) return [];

    const fields = mapEquipmentAndQuoteListToInvoiceTable(VOId, equipmentList, quote);
    setPriceCalc(quoteChargeCalculator.calc(fields));
    onSubscribeTotals?.(getCalculatedTotalsByLevel(fields), fields);
    return fields;
  }, [VOId, equipmentList, onSubscribeTotals, quote]);

  const {
    columns,
    handleAddRow,
    handleInsertRow,
    tableData,
    form,
    handleRemoveRow,
    onRemove,
    handleUpdateRow,
    handleMoveRow,
  } = useListing<QuoteChargeListingEntity, QuoteChargeListingContext>(
    {
      headerColumns: invoiceDetailsListingColumns,
      bodyFields: initialFields,
    },
    {
      shouldUnregister: false,
      onSubscribe: value => {
        setPriceCalc(quoteChargeCalculator.calc(value));
        onSubscribeTotals?.(getCalculatedTotalsByLevel(value), value);
      },
    }
  );

  useImperativeHandle(chargesSubmitRef, () => ({
    submit: () => form.getValues(),
  }));

  useEffect(() => {
    const subscription = form.watch((value, info) => {
      if (value.bodyFields && info.type === 'change') {
        const result = quoteChargeCalculator.calc(value.bodyFields as QuoteChargeListingEntity[]);

        setPriceCalc(prev => {
          if (!isEqual(prev, result)) return result;
          return prev;
        });
      }
    });

    return subscription.unsubscribe;
  }, [form]);

  const handleAddCharge = () => {
    handleOpenChargesDialog({
      onOpenCreateChargeDialog: () => {
        onOpenCreateChargeDialog({
          onConfirm: newCharge => {
            handleAddRow({
              equipment: null,
              type: QuoteChargeType.Invoice,
              charge: {
                financeType: 'other',
                price: newChargeRow,
                label: newCharge.name,
                type: QuoteFeeType.Other,
                attachTo: { id: VOId, type: ChargeAttachType.Invoice },
              },
            });

            handleClose();
          },
        });
      },
      onConfirm: charges => {
        handleClose();
        charges.forEach(charge =>
          handleAddRow({
            charge: {
              ...charge,
              type: charge.type,
              label: charge.label,
              financeType: chargeTypesByLabels[charge.label],
              attachTo: { id: VOId, type: ChargeAttachType.Invoice },
            },
            equipment: null,
            type: QuoteChargeType.Invoice,
          })
        );
      },
    });
  };

  const handleAddEquipment = () => {
    handleOpenEquipmentDialog({
      equipmentId: '',
      onCancel: handleCloseEquipmentDialog,
      onImagesSaved: setSelectedImages,
      onSubmit: async ({ createDTO }) => {
        handleCloseEquipmentDialog();

        const equipmentId = await onCreateEquipment({
          vendorOpportunityId: VOId,
          dynamicsVendorOpportunityId: dynamicsVOId,
          ...createDTO,
        });

        // Upload images if any were selected
        if (selectedImages.length > 0 && equipmentId) {
          await handleUploadImages(equipmentId, 'IMAGE', selectedImages);
          setSelectedImages([]); // Clear after upload
        }

        handleInsertRow(mapEquipmentToInvoiceTable({ equipmentId, ...createDTO }, quote), 0);
      },
    });
  };

  const handleEditEquipment = (equipmentId: string, rowIndex: number) => {
    handleOpenEquipmentDialog({
      equipmentId,
      onCancel: handleCloseEquipmentDialog,
      onSubmit: async ({ updateDTO }) => {
        handleCloseEquipmentDialog();

        await onUpdateEquipment({
          vendorOpportunityId: VOId,
          dynamicsVendorOpportunityId: dynamicsVOId,
          ...updateDTO,
        });

        handleUpdateRow(mapEquipmentToInvoiceTable(updateDTO, quote), rowIndex);
      },
    });
  };

  const handleSyncEquipment = async (equipmentForUpdate: Nullable<QuoteEquipmentWithCharge>) => {
    if (!equipmentForUpdate || !equipmentForUpdate.equipmentId) return;

    const targetEquipment = equipmentList.find(({ equipmentId }) => equipmentId === equipmentForUpdate.equipmentId);

    if (!targetEquipment) return;

    const isNeedToUpdate = checkingEquipmentUpgradeNeeds(equipmentForUpdate, targetEquipment);

    if (!isNeedToUpdate) return;

    await onUpdateEquipment({
      ...mapQuoteEquipmentToSync(equipmentForUpdate),
      vendorOpportunityId: VOId,
      equipmentId: targetEquipment.equipmentId,
      attachments: targetEquipment.attachments,
      dynamicsVendorOpportunityId: dynamicsVOId,
    });
  };

  const handleRemoveEquipment = (equipmentId: string, rowIndex: number) => {
    onDeleteEquipment(
      {
        equipmentId,
        vendorOpportunityId: VOId,
        dynamicsVendorOpportunityId: dynamicsVOId,
      },
      () => {
        onRemove(rowIndex);
      }
    );
  };

  const handleLinkCharge = (attachId: string, currentIndex: number, linkIndex: number) => {
    const currentRow = form.getValues(`bodyFields.${currentIndex}`);
    if (!currentRow.charge) return;

    const targetEquipment = equipmentList.find(({ equipmentId }) => equipmentId === attachId);

    const updatedRow = {
      ...currentRow,
      charge: {
        ...currentRow.charge,
        attachTo: {
          id: attachId,
          type: ChargeAttachType.Equipment,
          description: targetEquipment ? buildBadgeLabel(generateEquipmentName(targetEquipment)) : '',
        },
      },
    };

    handleUpdateRow(updatedRow, currentIndex);
    handleMoveRow(currentIndex, linkIndex);
  };

  const handleUnLinkCharge = (index: number) => {
    handleOpenConfirmationDialog({
      title: 'Confirmation',
      message: 'Are you sure you want to unlink this charge?',
      onConfirm: () => {
        const currentRow = form.getValues(`bodyFields.${index}`);
        if (!currentRow.charge) return;

        const updatedRow = {
          ...currentRow,
          charge: {
            ...currentRow.charge,
            attachTo: {
              id: VOId,
              type: ChargeAttachType.Invoice,
            },
          },
        };

        handleUpdateRow(updatedRow, index);
        handleMoveRow(index, tableData.length - 1);
      },
    });
  };

  return (
    <FormProvider {...form}>
      <Listing<QuoteChargeListingEntity, QuoteChargeListingContext>
        tableData={tableData}
        columns={columns}
        injectContext={{
          form,
          priceCalc,
          onRemoveRow: handleRemoveRow,
          onLinkCharge: handleLinkCharge,
          onUnLinkCharge: handleUnLinkCharge,
          onEditEquipment: handleEditEquipment,
          onSyncEquipment: handleSyncEquipment,
          onRemoveEquipment: handleRemoveEquipment,
          isAnyEquipmentActionInProgress: isAnyActionInProgress,
        }}
        actionPanelBottom={() =>
          actionPanelBottom({ onAddCharge: handleAddCharge, onAddEquipment: handleAddEquipment })
        }
      />
    </FormProvider>
  );
};
